import React, { createContext, useState, useContext, useEffect } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';

// Plugin natif local (android/.../AppSystemBarsPlugin.java) : colore la zone
// des barres système en bord à bord sans API Android obsolète.
const AppSystemBars = registerPlugin<{
  setBackgroundColor(options: { color: string }): Promise<void>;
}>('AppSystemBars');

type ThemeType = 'light' | 'dark';

const HEADER_COLOR_LIGHT = '#C87040';
const HEADER_COLOR_DARK = '#2E1A0A';

// Aligne la status bar native Android sur la couleur du header pour éviter
// une bande de couleur (bleu par défaut du template Capacitor) au-dessus
// du header sous edge-to-edge (Android 15+, ex. Pixel 8 Pro).
const syncNativeStatusBar = async (theme: ThemeType) => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    // Les icônes des barres restent claires (réglé côté natif) car les deux
    // couleurs de header sont foncées.
    await AppSystemBars.setBackgroundColor({
      color: theme === 'dark' ? HEADER_COLOR_DARK : HEADER_COLOR_LIGHT,
    });
  } catch {
    // Plugin indisponible (ex. web) : la barre reste gérée par le CSS
  }
};

interface ThemeContextType {
  theme: ThemeType;
  toggleTheme: () => void;
}

// Création du contexte avec des valeurs par défaut
const ThemeContext = createContext<ThemeContextType>({
  theme: 'light',
  toggleTheme: () => {},
});

// Hook personnalisé pour faciliter l'utilisation du contexte
export const useTheme = () => useContext(ThemeContext);

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Essayer de charger le thème depuis localStorage ou utiliser light par défaut
  const [theme, setTheme] = useState<ThemeType>(() => {
    const savedTheme = localStorage.getItem('theme');
    return (savedTheme === 'dark' || savedTheme === 'light') ? savedTheme : 'light';
  });

  // Fonction pour basculer entre les thèmes
  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  // Sauvegarder le thème dans localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('theme', theme);
    
    // ✅ AJOUT: Appliquer l'attribut data-theme pour les variables CSS
    document.documentElement.setAttribute('data-theme', theme);
    
    // Appliquer les classes au niveau du document pour les styles globaux
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
      document.body.classList.add('light');
      document.body.classList.remove('dark');
    }

    syncNativeStatusBar(theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

